import React, {useState, useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';

import Carousel from 'nuka-carousel';

import styled, {css} from 'styled-components';

import {ReactComponent as ArrowIcon} from './../../assets/arrow-colored.svg';
import {contentUrlSolver, slugify} from './../../helpers';
import {
    isMobile,
    mobileMediaQuery,
    notWebMediaQuery,
    PageBodyWrapper,
} from '../../styles';
import {getCurrentLang} from '../../utils/i18n';

const Container = styled(PageBodyWrapper)`
    background: #000;
    height: auto;
    padding: 0 40px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: row;
    overflow: hidden;
    position: relative;
    margin-bottom: 40px;

    ${notWebMediaQuery} {
        padding: 0;

        .slider-frame {
            padding-left: 40px !important;
        }
    }

    ${mobileMediaQuery} {
        height: 60px;
        padding: 0 !important;
        margin-bottom: 32px;

        .slider-frame {
            padding-left: 25px !important;
        }
    }
`;

const RoundedIcon = styled.button`
    flex: 0 0 73px;
    width: 73px;
    height: 90px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    margin: 0px;
    border: 0;
    background-color: transparent;
    &:not(:last-child) {
        margin-right: 1rem;
    }
    cursor: pointer;

    ${mobileMediaQuery} {
        flex: 0 0 60px;
        width: 60px;
        height: 60px;
        margin: 0px;
    }

    &:hover .show-icon {
        transform: scale(1.1);
    }

    &:hover .gradient {
        transform: scale(0.9);
    }

    &:hover .gray-background {
        transform: scale(0);
    }
`;

const ShowIcon = styled.img`
    object-fit: contain;
    width: 81px;
    margin-bottom: 3px;
    transition: all 0.2s ease-in-out;
    z-index: 2;

    ${mobileMediaQuery} {
        width: 60px;
    }
`;

const ShowBackground = styled.div`
    display: flex;
    position: absolute;
    width: 73px;
    height: 73px;
    z-index: 1;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease-in-out;

    background-image: radial-gradient(
        100% 316.05% at 0% 100%,
        #d437ec 0%,
        #ffd93f 18.4%,
        #5fc8f8 32.42%,
        #5fc8f9 57.14%,
        #ffd93f 87.58%,
        #d437eb 100%
    );

    ${props =>
        props.flip &&
        css`
            background-image: radial-gradient(
                100% 316.05% at 100% 0%,
                #d437ec 0%,
                #ffd93f 18.4%,
                #5fc8f8 32.42%,
                #5fc8f9 57.14%,
                #ffd93f 87.58%,
                #d437eb 100%
            );
        `}

    ${mobileMediaQuery} {
        width: 55px;
        height: 55px;
    }
`;

const GrayBackground = styled.div`
    position: absolute;
    background: #534b53;
    width: 67px;
    height: 67px;
    transition: all 0.2s ease-in-out;

    ${mobileMediaQuery} {
        width: 49px;
        height: 49px;
    }
`;

const CustomButtonBack = styled.div`
    position: absolute;
    width: 50px;
    border: none;
    top: 50%;
    transform: translateY(-50%);
    left: -50px;

    display: flex;
    justify-content: center;
    align-items: center;
    padding-right: 12px;
    cursor: pointer;

    svg {
        width: 18px;
        height: 28px;
        transform: scaleX(-1);

        margin: auto;
    }

    ${notWebMediaQuery} {
        display: none;
    }

    ${({disabled}) =>
        disabled &&
        css`
            pointer-events: none;
            opacity: 0.75;
        `}
`;

const CustomButtonNext = styled(CustomButtonBack)`
    left: initial;
    right: -50px;
    padding-right: 0px;
    padding-left: 12px;

    svg {
        transform: initial;
    }
`;

const ShowsCarroussel = ({shows, ...props}) => {
    const history = useHistory();
    const {t} = useTranslation();
    const {config, user} = useSelector(state => state);
    const idp = user && user.idp;

    const handleClickShow = show_title => {
        history.push(`/shows/${slugify(show_title)}`);
    };

    const [visibleSlides, setVisibleSlides] = useState(
        Math.floor((window.innerWidth - 120) / (isMobile ? 60 : 102)),
    );

    useEffect(() => {
        window.addEventListener('resize', calculateVisibleSlides);

        return () => {
            window.removeEventListener('resize', calculateVisibleSlides);
        };

        // eslint-disable-next-line
    }, []);

    const calculateVisibleSlides = () => {
        const showButtonWidth = isMobile ? 92 : 86;

        setVisibleSlides(
            Math.floor(
                (window.innerWidth - (isMobile ? 30 : 120)) / showButtonWidth,
            ),
        );
    };

    const sendAnalyticsEvent = (action = null, show = null) => {
        let data = {};
        if (show) {
            data = {
                franchise: show,
                contentclickevent: 1,
                shownavclickevent: 1,
            };
        }
        window.turner_metadata.trackAction.push({
            type: 'pageview',
            data: {
                ...data,
                pageName: 'cnplus.com/home',
                section: 'Home',
                subsection: 'nvs',
                mvpd: idp || 'idp not loaded',
                userlang: getCurrentLang(),
                domain: 'cnplus.com',
                region: 'latam',
                country:
                    user && user.countryIsoCode && user.countryName
                        ? `${user.countryIsoCode.toLowerCase()}|${
                              user.countryName
                          }`
                        : 'not loaded',
                brand: 'cartoon network',
                contenttype: 'Show Nav',
                contenttitle: show || action || 'ShowNav',
                platform: 'web',
                interaction: show ? 'show nav click' : 'show nav arrow click',
                interactionevent: 1,
            },
        });
    };

    return shows === undefined ? null : (
        <Container>
            <Carousel
                disableEdgeSwiping
                heightMode="max"
                scrollMode="remainder"
                slidesToScroll="auto"
                cellSpacing={isMobile ? 5 : 5}
                slideWidth={`${isMobile ? 60 : 86}px`}
                defaultControlsConfig={{
                    prevButtonStyle: {display: 'none'},
                    nextButtonStyle: {display: 'none'},
                    pagingDotsStyle: {display: 'none'},
                }}
                renderCenterLeftControls={props => (
                    <CustomButtonBack
                        disabled={props.currentSlide <= 0}
                        onClick={() => {
                            props.previousSlide();
                            sendAnalyticsEvent('Previous Arrow');
                        }}
                    >
                        <ArrowIcon title={t('controls.previous')} />
                    </CustomButtonBack>
                )}
                renderCenterRightControls={props => (
                    <CustomButtonNext
                        disabled={
                            shows &&
                            props.currentSlide - 2 >=
                                shows.length - visibleSlides
                        }
                        onClick={() => {
                            props.nextSlide();
                            sendAnalyticsEvent('Next Arrow');
                        }}
                    >
                        <ArrowIcon title={t('controls.next')} />
                    </CustomButtonNext>
                )}
            >
                {shows.map((show, index) => {
                    return (
                        <RoundedIcon
                            key={show.id}
                            title={show.title}
                            onClick={_ => {
                                handleClickShow(show.title);
                                sendAnalyticsEvent('Show Click', show.title);
                            }}
                            backgroundUrl={contentUrlSolver(
                                config,
                                show.image,
                                'SHOW-SELECTOR',
                                'png',
                            )}
                        >
                            <ShowBackground
                                className="gradient"
                                flip={index % 2 === 0}
                            >
                                <GrayBackground className="gray-background" />
                            </ShowBackground>

                            <ShowIcon
                                className="show-icon"
                                src={contentUrlSolver(
                                    config,
                                    show.image,
                                    'SHOW-SELECTOR',
                                    'png',
                                )}
                            />
                        </RoundedIcon>
                    );
                })}
            </Carousel>
        </Container>
    );
};

export default ShowsCarroussel;
